// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Retry Dialog for I-9 documents
 *
 * Re-queues the selected records for processing, with an optional crop override
 * (see useCropOverride).
 */
import type { FC } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
} from '@/components/ui'
import { RotateCcw } from 'lucide-react'
import { useCropOverride, type CropValues } from './CropOverride'

export type { CropValues }

interface RetryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCount: number
  isRetrying: boolean
  // null = no override (use the host's configured band)
  onConfirm: (crop: CropValues | null) => void
}

export const RetryDialog: FC<RetryDialogProps> = ({
  open,
  onOpenChange,
  selectedCount,
  isRetrying,
  onConfirm,
}) => {
  const { crop, isValid, element } = useCropOverride(open)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5" />
            Retry {selectedCount} {selectedCount === 1 ? 'document' : 'documents'}
          </DialogTitle>
          <DialogDescription>
            Re-queue the selected records for processing. By default the server&apos;s
            configured crop is used; override it only if the default band misses the
            fields (e.g. an older-format scan).
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">{element}</div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(crop)} disabled={!isValid || isRetrying || selectedCount === 0}>
            <RotateCcw className="h-4 w-4 mr-1" />
            Retry
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
